/**
 * PostMessageConduit is a singleton class that facilitates communication
 * between the main window and multiple iframes using the postMessage API.
 *
 * It allows registration of listeners for specific iframes identified by
 * unique listener IDs and ID keys. When a message is received, it checks
 * for registered listeners and invokes the appropriate callback if the
 * message origin matches the iframe's origin.
 */
class PostMessageConduit {
  constructor() {
    this._listeners = new Map();
    this._boundReceiveMessage = this._receiveMessage.bind(this);
    window.addEventListener('message', this._boundReceiveMessage);
  }

  static getInstance(createIfMissing = true) {
    if (!createIfMissing && !window.__PostMessageConduitInstance) {
      return null;
    }
    if (!window.__PostMessageConduitInstance) {
      window.__PostMessageConduitInstance = new PostMessageConduit();
    }
    return window.__PostMessageConduitInstance;
  }

  // Global message event handler.
  _receiveMessage(event) {
    let message = event.data;
    if (message?.source === 'react-devtools-content-script') return;

    // Attempt to parse message as JSON if it's a string.
    if (typeof message === 'string') {
      if (message.startsWith('{') || message.startsWith('[')) {
        try {
          message = JSON.parse(message);
        } catch (e) {
          // Not JSON, ignore.
          return;
        }
      }
    }

    if (!message || typeof message !== 'object') {
      return;
    }

    // Find the listeners for this message source including listeners registered for any source (global listeners)
    const listenerCallbacks = [
      ...this._listeners.get(event.source) || [],
      ...(event.source !== window ? (this._listeners.get(window) || []) : []), // Include global listeners
    ].filter(Boolean);
    if (!listenerCallbacks?.length) {
      // No callbacks registered for this source.
      return;
    }

    // Invoke all registered callbacks for this source
    for (const listenerCallback of listenerCallbacks) {
      listenerCallback(message, event);
    }
  }

  /**
   * Register a listener for messages from a specific iframe.
   * @param {*} params.iframeRef - Reference to the target iframe element.
   * @param {*} params.callback - Callback function to invoke on message receipt.
   */
  static registerIframeListener(iframeRef, callback) {
    const instance = PostMessageConduit.getInstance();

    const iframeWindow = iframeRef.contentWindow;
    if (!iframeWindow) {
      throw new Error('Iframe reference does not have a contentWindow');
    }

    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    const callbacks = instance._listeners.get(iframeWindow);
    if (callbacks) {
      callbacks.push(callback);
    } else {
      instance._listeners.set(iframeWindow, [ callback ]);
    }
  }

  static unregisterIframeListener(iframeRef, callback = null) {
    const instance = PostMessageConduit.getInstance(false);
    if (!instance) return;

    if (!iframeRef?.contentWindow) {
      return;
    }

    if (!callback) {
      // Remove all listeners for this iframeRef
      instance._listeners.delete(iframeRef.contentWindow);
      return;
    }

    const callbacks = instance._listeners.get(iframeRef.contentWindow);
    if (callbacks && callback) {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        instance._listeners.delete(iframeRef.contentWindow);
      }
    }
  }

  static registerGlobalListener(callback) {
    const instance = PostMessageConduit.getInstance();

    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function to register global listener');
    }

    const callbacks = instance._listeners.get(window);
    if (callbacks) {
      callbacks.push(callback);
    } else {
      instance._listeners.set(window, [ callback ]);
    }
  }

  static unregisterGlobalListener(callback) {
    const instance = PostMessageConduit.getInstance(false);
    if (!instance) return;
    if (!callback) {
      throw new Error('Callback must be provided to unregister global listener');
    }

    const callbacks = instance._listeners.get(window);
    if (callbacks && callback) {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        instance._listeners.delete(window);
      }
    }
  }

  static destroy() {
    const instance = PostMessageConduit.getInstance(false);
    if (!instance) return;

    window.removeEventListener('message', instance._boundReceiveMessage);

    // Clean up all references and remove the singleton instance.
    instance._boundReceiveMessage = null;
    instance._listeners.clear();
    window.__PostMessageConduitInstance = null;
  }
}

export default PostMessageConduit;
